import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const AdminEmailChange = ({ staffId, currentEmail, onSuccess, onCancel }) => {
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const getApiBaseUrl = () => {
    const url = process.env.REACT_APP_API_BASE_URL;
    if (!url || !url.startsWith("http")) {
      // Surface a clear error rather than falling back to a guessed host. The previous
      // fallback pointed at https://ogestyle.azurewebsites.net, which does not exist, so a
      // missing REACT_APP_API_BASE_URL produced a request to a dead host instead of a
      // diagnosable failure. Matches the canonical helper in utils/apiHelpers.js.
      throw new Error("API configuration error");
    }
    return url;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${getApiBaseUrl()}/api/email-change/admin-override/${staffId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ newEmail }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to change email");
      }

      if (onSuccess) onSuccess(newEmail);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "#f9fafb",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        marginTop: "16px",
      }}
    >
      <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>
        Change Email Address
      </h4>
      <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#6b7280" }}>
        Current email: <strong>{currentEmail}</strong>
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="newEmail"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            New Email Address
          </label>
          <input
            type="email"
            id="newEmail"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            placeholder="Enter new email address"
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        {error && (
          <div
            style={{
              padding: "10px 12px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "6px",
              color: "#991b1b",
              fontSize: "13px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              padding: "10px 16px",
              background: "#f3f4f6",
              color: "#374151",
              border: "none",
              borderRadius: "6px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              padding: "10px 16px",
              background: loading ? "#9ca3af" : "#00509d",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            {loading ? "Changing..." : "Change Email"}
          </button>
        </div>
      </form>

      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: "6px",
          fontSize: "12px",
          color: "#92400e",
        }}
      >
        <strong>⚠️ Admin Override:</strong> This will immediately change the
        staff member's email without verification. Notifications will be sent to
        both email addresses.
      </div>
    </div>
  );
};

export default AdminEmailChange;
